import React, { Fragment, useState } from "react";
import { PAGES, ACTIONS, usePermissions } from "hooks/permissions/permissions";
import { Select, Modal, Tooltip } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import moment from "moment";
import NumberCounter from "helpers/numberCounter";
import AttendanceTableListing from "./component/attendanceTableListing";
import { useDashboardCount, useDashboardDropdown, useDashboardEmployee } from "hooks/dashboard/dashboard";
import { AttendanceIcon, UsersIcon, AbsentIcon, SiteIcon } from "components/icons";
import "./style.css";
import DashboardCard from "./coordinate/card";
import ManpowerForecast from "./component/manpowerForecast";

const { Option } = Select;

const getInitials = (name) => {
    if (!name) return "?";
    return name.trim().split(/\s+/).slice(0, 2).map((word) => word[0]).join("").toUpperCase();
};

const Dashboard = () => {

    // Permissions arrive after first render; can() denies until then, so the search doesn't flash and vanish.
    const { can } = usePermissions();
    const canSearchEmployee = can(PAGES.DASHBOARD, ACTIONS.VIEW);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

    const { data: dashboardData } = useDashboardCount();
    const { data: dashboardDropDownData } = useDashboardDropdown();
    const { data: dashboardEmployee, isFetching: employeeLoading, isError: employeeError } = useDashboardEmployee(selectedEmployeeId);

    const [isModalVisible, setIsModalVisible] = useState(false);

    const employee = dashboardEmployee?.data;
    const noReading = !employee || Object.values(employee).every(value => value === null);
    const onSite = dashboardData?.data?.todayAttendance || 0;
    const totalEmployees = dashboardData?.data?.totalEmployees || 0;
    const allEmployees = dashboardData?.data?.allEmployees || 0;
    const absent = Math.max(totalEmployees - onSite, 0);
    const siteCount = dashboardData?.data?.siteData?.length || 0;
    const onSiteShare = totalEmployees ? Math.round((onSite / totalEmployees) * 100) : 0;

    const onhandleChange = (value) => {
        if (value) {
            setSelectedEmployeeId(value);
            setIsModalVisible(true);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    return (
        <Fragment>
            <div className="dashboard-partent">

                <header className="dash-topbar">
                    <div>
                        <span className="dash-topbar-eyebrow">Employee Tracking System</span>
                        <h1>Today&apos;s overview</h1>
                    </div>
                    <div className="dash-topbar-right">
                        <span className="dash-topbar-date">{moment().format("dddd, DD MMM YYYY")}</span>
                        <Tooltip title="RFID reader setup manual (PDF)">
                            <a className="dash-topbar-manual" href={`${process.env.PUBLIC_URL}/docs/invengo-reader-setup-manual.pdf`}
                                target="_blank" rel="noopener noreferrer" aria-label="RFID reader setup manual (PDF)">
                                <FilePdfOutlined />
                            </a>
                        </Tooltip>
                        {!canSearchEmployee ? null :
                            <Select showSearch allowClear onChange={onhandleChange} placeholder="Select Employee"
                                filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}>
                                {dashboardDropDownData?.data?.map((item, e) => (
                                    <Option key={e} value={item?.employeeId}>{item?.employeeName}</Option>
                                ))}
                            </Select>}
                    </div>
                </header>

                <div className="dash-grid">
                    <aside className="dash-rail">
                        <span className="dash-eyebrow">Sites</span>
                        <div className="dash-rail-scroll">
                            <DashboardCard />
                        </div>
                    </aside>

                    <div className="dash-main">
                        <div className="dash-kpis">
                            <article className="dash-kpi is-primary">
                                <span className="dash-kpi-icon"><AttendanceIcon /></span>
                                <span className="dash-kpi-body">
                                    <span className="dash-kpi-label">On site today</span>
                                    <span className="dash-kpi-value">
                                        <NumberCounter targetNumber={onSite} />
                                        <em>/{totalEmployees}</em>
                                    </span>
                                    <span className="dash-kpi-foot">{onSiteShare}% of the workforce</span>
                                </span>
                            </article>

                            <article className="dash-kpi is-mint">
                                <span className="dash-kpi-icon"><UsersIcon /></span>
                                <span className="dash-kpi-body">
                                    <span className="dash-kpi-label">Active employees</span>
                                    <span className="dash-kpi-value">{totalEmployees}<em>/{allEmployees}</em></span>
                                    <span className="dash-kpi-foot">Active with an RFID card, of {allEmployees} registered</span>
                                </span>
                            </article>

                            <article className="dash-kpi is-amber">
                                <span className="dash-kpi-icon"><AbsentIcon /></span>
                                <span className="dash-kpi-body">
                                    <span className="dash-kpi-label">Not on site</span>
                                    <span className="dash-kpi-value">{absent}</span>
                                    <span className="dash-kpi-foot">No check-in recorded today</span>
                                </span>
                            </article>

                            <article className="dash-kpi is-violet">
                                <span className="dash-kpi-icon"><SiteIcon /></span>
                                <span className="dash-kpi-body">
                                    <span className="dash-kpi-label">Active sites</span>
                                    <span className="dash-kpi-value">{siteCount}</span>
                                    <span className="dash-kpi-foot">Reporting attendance</span>
                                </span>
                            </article>
                        </div>

                        <ManpowerForecast horizonDays={14} />

                        <AttendanceTableListing title="Today's Attendance" type={1} />
                    </div>
                </div>

            </div>

            <Modal
                title={null}
                footer={null}
                centered
                width={400}
                className="dashboard-modal emp-modal"
                visible={isModalVisible}
                onCancel={handleCancel}
            >
                {employeeLoading ? (
                    <div className="emp-modal-empty">
                        <span className="table-state-spinner" role="status" aria-label="Loading" />
                        <p>Loading employee location…</p>
                    </div>
                ) : employeeError || noReading ? (
                    <div className="emp-modal-empty">
                        <span className="emp-modal-empty-icon"><UsersIcon /></span>
                        <h4>No data found</h4>
                        <p>No recent location reading for this employee.</p>
                    </div>
                ) : (
                    <Fragment>
                        <div className="emp-modal-hero">
                            <span className="emp-modal-avatar">{getInitials(employee?.employeeName)}</span>
                            <span className="emp-modal-identity">
                                <span className="emp-modal-name">{employee?.employeeName || "Unknown employee"}</span>
                                <span className="emp-modal-code">{employee?.employeeCode || "N/A"}</span>
                            </span>
                        </div>

                        <div className="emp-modal-grid">
                            <div className="emp-modal-row">
                                <span className="emp-modal-label">Site</span>
                                <span className="emp-modal-value">{employee?.siteName || "N/A"}</span>
                            </div>
                            <div className="emp-modal-row">
                                <span className="emp-modal-label">Location</span>
                                <span className="emp-modal-value">{employee?.locationName || "N/A"}</span>
                            </div>
                            <div className="emp-modal-row">
                                <span className="emp-modal-label">Last known time</span>
                                {/* Trim seconds and milliseconds as text, so the site-local time is never re-zoned. */}
                                <span className="emp-modal-value">
                                    {employee?.lastKnownTime
                                        ? String(employee.lastKnownTime).replace(/(\d{1,2}:\d{2}):\d{2}(\.\d+)?/, "$1")
                                        : "N/A"}
                                </span>
                            </div>
                        </div>
                    </Fragment>
                )}
            </Modal>
        </Fragment>
    );
};
export default Dashboard;
